import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import { formatTimer } from 'app/utils/entities/match_data';
import Dota2DayIcon from 'app/icons/dota2_sun.svg';
import Dota2NightIcon from 'app/icons/dota2_moon.svg';

import { getDota2Period } from './utils';
import style from './style.module.scss';

interface Props extends ClassNameProp {
  timer: Timer;
  videogame: VideogameSlug;
  live?: boolean;
}

const GameTimer: FC<Props> = ({ timer, videogame, live = false, className }) => {
  const factor = timer.acceleration_factor ?? 1;
  const decrement = timer.direction === 'decrement';

  const firstValue = useMemo(
    () =>
      timer.paused || !timer.issued_at || timer.type === 'snapshot' || timer.timer === null
        ? timer.timer ?? 0
        : timer.timer +
          Math.round((new Date().getTime() - new Date(timer.issued_at).getTime()) / 1000) *
            factor *
            (decrement ? -1 : 1),
    [timer]
  );

  const [timerValue, setTimerValue] = useState(firstValue);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    setTimerValue(firstValue);

    if (live && !timer.paused && timer.type !== 'snapshot') {
      interval = setInterval(() => {
        setTimerValue(timerValue => timerValue + factor * (decrement ? -1 : 1));
      }, 1000);
    }

    return () => {
      clearInterval(interval);
    };
  }, [timer]);

  if (timer.timer === null) {
    return null;
  }

  const formattedTimer = formatTimer(timerValue);
  const period = videogame === 'dota-2' ? getDota2Period(formattedTimer) : null;

  return (
    <div className={classNames(style.timer, className)}>
      {period === 'day' && <Dota2DayIcon className={style.timerIcon} />}
      {period === 'night' && <Dota2NightIcon className={style.timerIcon} />}
      {formattedTimer}
    </div>
  );
};

GameTimer.displayName = 'GameTimer';

export default GameTimer;
